import React, { Suspense, useRef, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, Center, Html, Stage } from '@react-three/drei';

// Preload the model if the path is static and known
// useGLTF.preload('/path/to/your/default/model.glb');

// --- Helper: Annotation Label Component ---
const AnnotationLabel = ({ children, ...props }) => (
  <Html
    as='div' // Render uses a div
    center // Center the HTML relative to the attachment point
    // distanceFactor={10} // Optional: Make text smaller further away
    // occlude // Optional: Hide text when behind other objects
    // portal={/* useRef() */} // Optional: Render in a portal
    style={{
      backgroundColor: 'rgba(50, 50, 50, 0.7)',
      color: 'white',
      padding: '2px 5px',
      borderRadius: '3px',
      fontSize: '10px',
      whiteSpace: 'nowrap',
      userSelect: 'none', // Prevent text selection
      pointerEvents: 'none' // Allow clicking through the label
    }}
    {...props} // Pass other Html props
  >
    {children}
  </Html>
);

// --- Original Model Component (restored for simplicity) ---
function Model({ modelPath }) {
  const { scene } = useGLTF(modelPath); // Keep useGLTF simple
  // Ref for the group to rotate
  const groupRef = useRef();

  // useFrame hook for animation on each frame
  useFrame((state, delta) => {
    if (groupRef.current) {
      // Rotate the model slowly around the Y-axis
      groupRef.current.rotation.y += delta * 0.2; // Adjust speed (delta * factor)
    }
  });

  // Wrap scene in a group and attach the ref
  return (
    <group ref={groupRef}>
      <primitive object={scene} />
    </group>
  );
}

// --- Component to Render Annotations --- 
function AnnotationsRenderer({ annotations }) {
  // Directly renders labels at specified positions
  return (
    <group>
      {annotations.map(({ key, text, position }) => {
        return position ? (
          <AnnotationLabel key={key} position={position}>
            {text}
          </AnnotationLabel>
        ) : null; 
      })}
    </group>
  );
}

const SimpleModelViewer = ({ modelPath }) => {
  // --- Define Annotations with hardcoded positions ---
  const annotations = [
    { key: 'jp', text: 'Integrated Jet Pump', position: [0.5, 0.5, 0.5] }, // Arbitrary position
    { key: 'bs', text: 'Swappable Battery System', position: [-0.5, 0.3, 0.5] }, // Arbitrary position
    { key: 'sf', text: 'Advanced Safety Features', position: [0, 0, 1.0] }, // Arbitrary position
    { key: 'cd', text: 'Compact Design', position: [0, -0.5, 0.5] }, // Arbitrary position
  ];

  return (
    <div 
      style={{ height: '400px', width: '100%', background: 'transparent' }} 
      // Removed onWheel handler to allow mobile scroll
    >
      <Canvas 
        shadows 
        camera={{ position: [0, 1, 3], fov: 50 }} 
        style={{ background: 'transparent' }}
      >
        <Suspense fallback={<ModelLoader />}>
          
          <Stage environment="city" intensity={0.6}> 
             {/* Render model without onRotate prop */}
             <Model modelPath={modelPath} /> 
          </Stage>
          
          {/* Render annotations at fixed positions */}
          <AnnotationsRenderer annotations={annotations} />

        </Suspense>
      </Canvas>
    </div>
  );
};

// Simple fallback loader component
const ModelLoader = () => (
  // Drei's <Html> component allows rendering HTML inside the Canvas
  <mesh> 
    <boxGeometry args={[1, 1, 1]} />
    <meshStandardMaterial wireframe color="royalblue" />
  </mesh>
);

export default SimpleModelViewer; 